/**
 * Verbatim tooltip copy for the 14 Executive Summary Overview metrics.
 * DO NOT paraphrase, shorten, or rewrite. This file is the single source of truth.
 *
 * Each metric has:
 *   info:  factual definition + calculation
 *   help:  rule-of-thumb interpretation guidance
 *   accountingNote: true when the metric derives from accounting data
 *                   (Revenue, Revenue Growth MoM, Gross Margin %, Net Margin %, Ending Cash, Overdue AR %)
 */
export const METRIC_TOOLTIPS = {
  // 1. Overall Business Health Score
  health_score: {
    info: "A composite score (0-100) that rolls up profitability, cash strength, and sales pipeline signals for the selected date range.",
    help: "Rule of thumb: 80-100 = strong, 60-79 = watch, <60 = at-risk. Use the trend: a drop of 10+ points vs the prior period is a prompt to drill into profitability, cash, or pipeline drivers.",
    accountingNote: false,
  },
  // 2. Forward Revenue Risk
  forward_revenue_risk: {
    info: "A forward-looking signal that combines recent revenue trend and CRM pipeline indicators (pipeline coverage, win rate, forecast) to estimate near-term revenue risk.",
    help: "Lower risk is better. If risk stays elevated for 2+ periods, focus on demand generation (lead volume), deal quality (conversion/win rate), and pipeline coverage versus targets.",
    accountingNote: false,
  },
  // 3. Cash Pressure Risk
  cash_pressure_risk: {
    info: "A signal that combines cash trend, AR/AP aging, and cash-flow momentum to estimate near-term cash pressure.",
    help: "Lower risk is better. If risk is rising, prioritize collections (overdue AR), review payment terms, and reduce discretionary spend. Escalate quickly if runway is under 2 months.",
    accountingNote: false,
  },
  // 4. Market Headwind / Tailwind
  market_headwind: {
    info: "A context indicator summarizing whether macro and industry conditions are improving or worsening (based on selected economic and industry drivers).",
    help: "This does not score your business - it scores the environment. A headwind suggests planning more conservatively; a tailwind may support growth. Compare your revenue and pipeline trends to the market direction.",
    accountingNote: false,
  },
  // 5. Revenue
  revenue: {
    info: "Total income for the selected period from the Profit & Loss (sum of accounts mapped to Revenue).",
    help: "Higher is generally better. Look at MoM/YoY trends; compare to peer benchmarks when available. Large swings can be seasonal or driven by pricing and volume changes.",
    accountingNote: true,
  },
  // 6. Revenue Growth (MoM)
  revenue_growth_mom: {
    info: "Percent change in revenue compared with the prior period (typically prior month).",
    help: "Rule of thumb: >0% is healthy; 5-20% is strong for many SMBs (varies by industry). Two or more negative months in a row is a warning sign unless seasonal.",
    accountingNote: true,
  },
  // 7. Gross Margin %
  gross_margin: {
    info: "Gross profit divided by revenue (gross profit per sales dollar).",
    help: "Higher is better. Compare to your peer benchmark range when available. Rule of thumb: many service firms target 30-60%; many product/mixed firms target 20-50% (industry varies).",
    accountingNote: true,
  },
  // 8. Net Margin %
  net_margin: {
    info: "Net income divided by revenue (profit after all expenses per sales dollar).",
    help: "Higher is better. Rule of thumb: 5-15% is often healthy for stable SMBs; 0-5% is thin; negative is a sustained risk. Always compare to peers and your own history.",
    accountingNote: true,
  },
  // 9. Ending Cash
  ending_cash: {
    info: "Total cash in bank accounts as of the selected date from the Balance Sheet.",
    help: "More cash provides flexibility. Rule of thumb: maintain at least 1-3 months of average operating expenses; 3-6 months if revenue is volatile, seasonal, or you are in a growth/investment phase.",
    accountingNote: true,
  },
  // 10. Runway (Months)
  runway_months: {
    info: "Estimated months before cash runs out based on current cash balance divided by average monthly burn (net cash outflow).",
    help: "Rule of thumb: 6+ months is comfortable, 3-6 months is workable, 1-3 months is tight, <1 month is urgent. Use alongside collections and cost actions.",
    accountingNote: false,
  },
  // 11. Overdue AR %
  overdue_ar: {
    info: "Share of accounts receivable that is past due (non-current aging buckets) divided by total AR.",
    help: "Lower is better. Rule of thumb: <10% is strong, 10-25% watch, >25% indicates meaningful collection friction and higher cash risk.",
    accountingNote: true,
  },
  // 12. Weighted Pipeline $
  weighted_pipeline: {
    info: "Open pipeline adjusted by stage probability (each deal amount multiplied by its stage probability).",
    help: "Higher is generally better and more realistic than raw pipeline. Rule of thumb: many B2B teams aim for 2-4x coverage of the next 60-90 day target (depends on win rate and sales cycle).",
    accountingNote: false,
  },
  // 13. Win Rate
  win_rate: {
    info: "Closed-won deals divided by all closed deals (won + lost) in the selected period.",
    help: "Higher is better. Rule of thumb: 20-30% is common; 30-50% is strong; <15% often signals lead quality, pricing, or qualification issues (varies by market).",
    accountingNote: false,
  },
  // 14. Forecast (Next Period)
  forecast_next_period: {
    info: "Expected near-term bookings based on weighted pipeline and expected close dates (directional estimate).",
    help: "Use as a directional view, not a guarantee. If forecast is below target for multiple weeks, increase lead flow, tighten qualification, and improve follow-up speed.",
    accountingNote: false,
  },

  /* -----------------------------------------------------------------
   * Financial Operations > Profitability. SMB-friendly tooltip copy.
   * Plain English, no accountant jargon. See client spec table.
   * ----------------------------------------------------------------- */
  fo_revenue: {
    info: "All the money your business earned in this period.",
    help: "Higher is better, but compare against your prior periods. If revenue holds flat while profits drop, your costs are climbing or your prices are slipping.",
    accountingNote: false,
  },
  fo_revenue_growth_mom: {
    info: "How much your revenue grew or shrank versus last month.",
    help: "Steady positive growth means you're healthy and expanding. Two or more negative months in a row means you should check your sales pipeline and customer retention.",
    accountingNote: false,
  },
  fo_revenue_growth_yoy: {
    info: "How this period compares to the same period last year.",
    help: "Beating peers means you're outperforming your market; trailing them means you're losing ground. If YoY is down but MoM is up, you're recovering. Keep the momentum.",
    accountingNote: false,
  },
  fo_gross_profit: {
    info: "What's left after you pay the direct costs of delivering your product or service.",
    help: "Higher is better. If gross profit drops faster than revenue, your delivery costs are climbing or some expenses are coded wrong in your books.",
    accountingNote: false,
  },
  fo_gross_margin: {
    info: "What share of every sales dollar you keep after direct delivery costs.",
    help: "Service businesses should aim for 30-60%; product or mixed businesses for 20-50%. If yours is falling, raise prices, cut delivery costs, or check how expenses are categorized.",
    accountingNote: false,
  },
  fo_cogs: {
    info: "The direct costs of delivering what you sell (materials, delivery labor, etc.).",
    help: "Keep this growing slower than your revenue. If COGS is climbing faster, renegotiate with suppliers, fix labor inefficiency, or check that expenses are sorted correctly.",
    accountingNote: false,
  },
  fo_cogs_pct: {
    info: "How much of every sales dollar gets eaten by direct delivery costs.",
    help: "Lower is better, and being above your peer range is a warning sign. Drill into supplier pricing, discounting habits, and whether some overhead is mislabeled as COGS.",
    accountingNote: false,
  },
  fo_operating_income: {
    info: "Profit from your day-to-day operations, before interest and taxes.",
    help: "Higher is better. If yours trails your peers, look at overhead, your pricing, and how efficiently you're delivering.",
    accountingNote: false,
  },
  fo_operating_margin: {
    info: "What share of every sales dollar becomes operating profit.",
    help: "Aim for 10%+ as strong, 5-10% as reasonable, under 5% as thin. If yours is dropping, raise prices, trim overhead, or compare against your own historical baseline to find the leak.",
    accountingNote: false,
  },
  fo_ebitda: {
    info: "Earnings before interest, taxes, depreciation, and amortization. A quick read on your operating cash strength.",
    help: "Higher is better. Benchmarks only mean something when you compare to peers who book expenses the same way you do.",
    accountingNote: false,
  },
  fo_ebitda_margin: {
    info: "What share of every sales dollar becomes EBITDA.",
    help: "Aim for 10-20% as healthy for most SMBs; under 10% gets tight. If yours is slipping, find the cost driver and fix it before it hits net profit.",
    accountingNote: false,
  },
  fo_net_income: {
    info: "The actual profit left after every expense, tax, and interest payment.",
    help: "Higher is better. If you're losing money for several periods, your business isn't paying for itself. Review pricing, cost structure, and how fast you're collecting.",
    accountingNote: false,
  },
  fo_net_margin: {
    info: "What share of every sales dollar you actually keep as profit.",
    help: "5-15% is healthy, 0-5% is thin, and negative is a real risk unless you're deliberately investing for growth. If you're below 5%, raise prices or cut a clear line of costs.",
    accountingNote: false,
  },
  fo_opex: {
    info: "Your overhead. The costs of running the business, not delivering the product.",
    help: "Keep this growing slower than revenue, but cutting too deep starves the business. If OpEx is outpacing revenue, audit headcount, software subscriptions, and discretionary spend.",
    accountingNote: false,
  },
  fo_opex_ratio: {
    info: "What share of every sales dollar goes to overhead.",
    help: "Most SMB service firms run 20-40%. If yours is climbing, overhead is creeping. Look at headcount, subscriptions, and admin spend before margins suffer.",
    accountingNote: false,
  },
  fo_payroll: {
    info: "Total money you paid your team in this period (wages, taxes, benefits).",
    help: "Good levels depend on your industry, but payroll must not grow faster than revenue. If it does, raise prices, improve team utilization, or rethink your hiring pace.",
    accountingNote: false,
  },
  fo_payroll_pct: {
    info: "What share of every sales dollar goes to your team's pay.",
    help: "Service businesses usually run 30-60%. If yours is climbing, your margins are at risk. Fix it by raising prices, improving how efficiently your team works, or both.",
    accountingNote: false,
  },

  /* -----------------------------------------------------------------
   * Financial Operations > Cash and Working Capital
   * camelCase keys per spec. Verbatim copy. Do not modify.
   * ----------------------------------------------------------------- */
  endingCash: {
    info: "Total cash in bank accounts as of the selected date from the Balance Sheet.",
    help: "Rule of thumb: keep 1-3 months of operating expenses available; 3-6 months if revenue is volatile/seasonal or you are investing for growth.",
    accountingNote: true,
  },
  netCashFlow: {
    info: "Net change in cash over the selected period (cash in minus cash out).",
    help: "Positive is generally better. Negative cash flow for several periods can indicate profitability or collections issues; use AR/AP and margins to diagnose.",
    accountingNote: true,
  },
  daysCashOnHand: {
    info: "Estimated number of days the business could operate using current cash based on recent average daily expenses.",
    help: "Higher is safer. Rule of thumb: 30-90 days is common; <30 days is higher risk; 90+ days provides flexibility (industry varies).",
    accountingNote: false,
  },
  burnRate: {
    info: "Average monthly net cash outflow over the selected lookback period.",
    help: "Lower burn is better. Rising burn plus falling cash suggests the need for pricing, cost reduction, or collections action.",
    accountingNote: false,
  },
  runway: {
    info: "Estimated months before cash runs out: cash balance divided by monthly burn.",
    help: "More runway is safer. Rule of thumb: 6+ months comfortable, 3-6 workable, 1-3 tight, <1 urgent.",
    accountingNote: false,
  },
  arBalance: {
    info: "Total unpaid invoices (money owed by customers) as of the selected date.",
    help: "AR grows with sales, but overdue AR should stay low. If AR is rising while cash is falling, improve invoicing cadence and collections follow-up.",
    accountingNote: true,
  },
  overdueArPercent: {
    info: "Share of AR that is past due (non-current aging buckets) divided by total AR.",
    help: "Lower is better. Rule of thumb: <10% strong; 10-25% watch; >25% high risk.",
    accountingNote: true,
  },
  dso: {
    info: "Average number of days it takes to collect from customers, calculated using AR and revenue (or a credit-sales proxy).",
    help: "Lower is better. Rule of thumb: target at or below your payment terms; <30 days is strong; 30-45 ok; >60 often indicates collection friction.",
    accountingNote: false,
  },
  apBalance: {
    info: "Total unpaid vendor bills (money you owe) as of the selected date.",
    help: "AP can rise with growth, but overdue AP should stay low. Rising overdue AP can damage vendor relationships and is a cash-pressure signal.",
    accountingNote: true,
  },
  overdueApPercent: {
    info: "Share of AP that is past due (non-current aging buckets) divided by total AP.",
    help: "Lower is better. Rule of thumb: <10% strong; 10-25% watch; >25% indicates meaningful cash pressure or process issues.",
    accountingNote: true,
  },
  dpo: {
    info: "Average number of days the business takes to pay vendors, calculated using AP and spend (or a spend proxy).",
    help: "Context matters. Many businesses run 30-60 days depending on terms. Paying slower preserves cash but can strain vendors; paying faster supports vendors but uses cash sooner.",
    accountingNote: false,
  },
  workingCapital: {
    info: "Current assets minus current liabilities (short-term financial flexibility).",
    help: "Higher is generally better. Negative working capital can be normal in some models (subscriptions/fast cash collection) but risky in others. Compare to peers and watch the trend.",
    accountingNote: true,
  },
  currentRatio: {
    info: "Current assets divided by current liabilities (short-term liquidity).",
    help: "Rule of thumb: 1.2-2.0 is often healthy; <1.0 can indicate short-term pressure; >3.0 can indicate idle working capital (industry varies).",
    accountingNote: false,
  },
  quickRatio: {
    info: "(Cash + AR and other quick assets) divided by current liabilities (liquidity excluding inventory).",
    help: "Rule of thumb: 1.0+ is typically healthy; 0.8-1.0 watch; <0.8 higher risk (industry varies).",
    accountingNote: false,
  },
  cashConversionCycle: {
    info: "Approximate days to convert cash spent into cash collected: DSO + Days Inventory - DPO (inventory term omitted if not applicable).",
    help: "Lower is better because cash returns faster. A negative cycle (collecting before paying) is usually strong. If cycle lengthens, focus on collections, inventory management, and payment terms.",
    accountingNote: false,
  },
  debtToEquity: {
    info: "Total liabilities divided by owners' equity (leverage).",
    help: "Lower is generally safer. Rule of thumb: 0-1 is moderate; 1-2 is higher leverage; >2 can be high risk unless stable cash flows (industry varies).",
    accountingNote: true,
  },
  interestCoverage: {
    info: "Ability to cover interest payments: operating earnings divided by interest expense.",
    help: "Higher is safer. Rule of thumb: 3x+ is healthy; 1.5-3x watch; <1.5x higher risk. Watch trend as rates change.",
    accountingNote: false,
  },
  daysInventoryOutstanding: {
    info: "Average number of days inventory is held before it is sold (inventory timing metric).",
    help: "Lower is generally better. If DIO is high vs peers, review purchasing, forecasting, and slow-moving inventory.",
    accountingNote: false,
  },
  inventoryTurnover: {
    info: "How often inventory is sold and replaced over a period (COGS divided by average inventory).",
    help: "Higher is generally better. Low turnover vs peers can indicate overstocking or slow-moving items.",
    accountingNote: false,
  },

  /* -----------------------------------------------------------------
   * Financial Operations > Profitability. camelCase keys per spec.
   * Verbatim copy. All 17 metrics have accountingNote: true.
   * profitabilityRevenue is intentionally distinct from `revenue`.
   * ----------------------------------------------------------------- */
  profitabilityRevenue: {
    info: "Total income for the selected period from your Profit & Loss (sum of accounts mapped to Revenue).",
    help: "Higher is generally better. Compare to prior periods and to peers. If revenue is flat but profits fall, costs may be rising or pricing may be under pressure.",
    accountingNote: true,
  },
  revenueGrowthMoM: {
    info: "Percent change in revenue versus the prior month (or prior comparable period).",
    help: "Rule of thumb: sustained positive growth is healthy. Multiple negative months in a row merits review of pipeline and retention.",
    accountingNote: true,
  },
  revenueGrowthYoY: {
    info: "Percent change in revenue compared with the same period last year (seasonality-aware).",
    help: "Above peer median suggests outperformance; below peer median suggests slower growth or market pressure. If YoY is down but MoM is up, you may be recovering from a weak prior year period.",
    accountingNote: true,
  },
  grossProfit: {
    info: "Revenue minus Cost of Goods Sold (COGS) for the selected period.",
    help: "Higher is better. If gross profit declines faster than revenue, delivery costs may be rising or costs may be misclassified.",
    accountingNote: true,
  },
  grossMarginPercent: {
    info: "Gross profit divided by revenue.",
    help: "Higher is better. Rule of thumb: service firms often target 30-60%; product/mixed firms 20-50% (industry varies). Use peer benchmarks when available.",
    accountingNote: true,
  },
  cogs: {
    info: "Total Cost of Goods Sold for the selected period (direct costs tied to delivering products/services).",
    help: "Lower is generally better as a percent of revenue. If COGS rises faster than revenue, review supplier costs, labor efficiency, and account mapping/classification.",
    accountingNote: true,
  },
  cogsPercentOfRevenue: {
    info: "COGS divided by revenue.",
    help: "Lower is generally better. Being above the peer range can indicate higher delivery costs, discounting, or misclassified expenses.",
    accountingNote: true,
  },
  operatingIncomeEbit: {
    info: "Profit after operating expenses (before interest and taxes) for the selected period.",
    help: "Higher is better. If EBIT is below peers, review overhead, pricing, and delivery efficiency.",
    accountingNote: true,
  },
  operatingMarginPercent: {
    info: "Operating income divided by revenue.",
    help: "Higher is better. Rule of thumb: 10%+ is strong for many SMBs; 5-10% is reasonable; <5% is thin (industry varies). Compare to peers and your historical baseline.",
    accountingNote: true,
  },
  ebitda: {
    info: "Earnings before interest, taxes, depreciation, and amortization (a cash-flow proxy for operating performance).",
    help: "Higher is better. Benchmarks are most meaningful when peers use similar accounting treatment and expense classification.",
    accountingNote: true,
  },
  ebitdaMarginPercent: {
    info: "EBITDA divided by revenue.",
    help: "Higher is better. Rule of thumb: 10-20% is often healthy for many SMBs; <10% may be tight (varies by industry). Use peer comparisons when available.",
    accountingNote: true,
  },
  netIncome: {
    info: "Bottom-line profit after all expenses for the selected period.",
    help: "Higher is better. Persistent losses indicate the business is not currently self-funding; review pricing, cost structure, and collections.",
    accountingNote: true,
  },
  netMarginPercent: {
    info: "Net income divided by revenue.",
    help: "Higher is better. Rule of thumb: 5-15% healthy; 0-5% thin; negative indicates risk unless temporarily investing for growth.",
    accountingNote: true,
  },
  operatingExpenses: {
    info: "Total operating expenses for the selected period (overhead and running costs).",
    help: "Lower is generally better relative to revenue, but too low can signal underinvestment. If OpEx rises faster than revenue, review headcount, subscriptions, and discretionary spend.",
    accountingNote: true,
  },
  opExRatio: {
    info: "Operating expenses divided by revenue.",
    help: "Lower is generally better. Rule of thumb: 20-40% is common for many SMB service firms; higher ratios may indicate overhead creep (industry varies). Use peer benchmarks where available.",
    accountingNote: true,
  },
  payrollExpense: {
    info: "Total payroll-related expense recorded in the accounting system for the selected period.",
    help: "Good ranges vary by industry. Watch for payroll growing faster than revenue or margin. If payroll is the main delivery engine (services), compare to peers and to gross margin targets.",
    accountingNote: true,
  },
  payrollPercentOfRevenue: {
    info: "Payroll expense divided by revenue.",
    help: "Rule of thumb: many service businesses run 30-60%. If this rises over time, margins are at risk unless pricing and utilization improve.",
    accountingNote: true,
  },

  /* =================================================================
   * OPP - Tool Tips Document 3 (Feb 2026 append).
   * Verbatim copy from `/app/memory/OPP_ToolTips_Doc3.md`.
   * Namespaced by section prefix so nothing collides with existing keys.
   * ================================================================= */

  /* Section 4. Operational Overview */
  ops_monthEndCloseCycleTime: {
    info: "Number of days from period end (month-end) until the books are closed for reporting (close completed date minus period end date).",
    help: "Rule of thumb: 3-10 business days is strong for many SMBs; 10-15 is workable; >15 is slow. Longer closes often reduce decision speed and can signal process gaps.",
    accountingNote: false,
  },
  ops_onTimeCloseRate: {
    info: "Percent of closes completed on or before the promised close deadline (completed_on_time closes divided by total closes).",
    help: "Higher is better. Rule of thumb: target 90%+; <80% suggests recurring bottlenecks (late documentation, approvals, or capacity constraints).",
    accountingNote: false,
  },
  ops_bankReconciliationCoverage: {
    info: "Percent of bank/credit accounts reconciled through the most recent statement period (reconciled accounts divided by total accounts).",
    help: "Higher is better. Rule of thumb: target 100% monthly. Less than full coverage can lead to errors in cash, expense classification, and reporting.",
    accountingNote: false,
  },
  ops_unreconciledTransactions: {
    info: "Count of transactions not yet matched or cleared in reconciliations as of the selected date.",
    help: "Lower is better. A growing backlog often indicates missing bank feeds, duplicate entries, or delayed categorization. Review weekly if the count is rising.",
    accountingNote: false,
  },
  ops_outstandingClientItems: {
    info: "Number of tasks or requests marked as waiting on client input (documents, approvals, answers) at the time of reporting.",
    help: "Lower is better. A persistently high number can delay closes and increase rework. Use this to drive a monthly client checklist and set clear cutoffs.",
    accountingNote: false,
  },
  ops_openRequestsBacklog: {
    info: "Number of open operational requests (tickets or tasks) not yet completed as of the selected date.",
    help: "Lower is better relative to normal volume. Watch trend and aging: a rising backlog signals capacity constraints or process issues.",
    accountingNote: false,
  },
  ops_overdueRequests: {
    info: "Number of open requests past their due date (overdue backlog).",
    help: "Lower is better. Rule of thumb: keep overdue under 5-10% of open work. Persistent overdue items are a service quality and retention risk.",
    accountingNote: false,
  },
  ops_slaBreachRate: {
    info: "Percent of requests completed after their SLA due date (breached tickets divided by total completed tickets).",
    help: "Lower is better. Rule of thumb: <5% strong; 5-10% watch; >10% indicates the team is over capacity or SLAs are mis-set.",
    accountingNote: false,
  },
  ops_firstResponseTime: {
    info: "Typical time from request creation to first response (median is used to reduce outliers).",
    help: "Lower is better. Rule of thumb: for normal priority, respond within 1 business day; for urgent items, within hours. Improving response time usually improves client satisfaction.",
    accountingNote: false,
  },
  ops_resolutionTime: {
    info: "Typical time from request creation to completion or resolution (median is used to reduce outliers).",
    help: "Lower is better. Compare by request type. If resolution time is rising, look for bottlenecks like approvals, missing data, or unclear scope.",
    accountingNote: false,
  },
  ops_reworkCorrectionRate: {
    info: "Percent of completed deliverables that require rework (reopened tickets or corrections divided by completed deliverables).",
    help: "Lower is better. Rule of thumb: <5-10% is healthy. Rising rework often indicates upstream data issues, unclear requirements, or training gaps.",
    accountingNote: false,
  },
  ops_payrollOnTimeRate: {
    info: "Percent of payroll runs processed or approved before the cutoff time required to pay employees on schedule.",
    help: "Higher is better. Rule of thumb: target 100%; anything below 98% should trigger root-cause review because late payroll is high-impact.",
    accountingNote: false,
  },
  ops_payrollAccuracyRate: {
    info: "Percent of payroll runs completed with zero corrections (no adjustments, voids, or off-cycle fixes required).",
    help: "Higher is better. Rule of thumb: 99-100% is the goal. A drop indicates upstream data problems (hours, rates, deductions) or process breakdown.",
    accountingNote: false,
  },

  /* Section 5. CRM (Sales and Pipeline) */
  crm_newLeads: {
    info: "Number of new contacts created in the CRM during the selected period.",
    help: "More leads can create future pipeline, but quality matters. Rule of thumb: track trend and conversion. If leads rise but opportunities do not, review targeting and follow-up speed.",
    accountingNote: false,
  },
  crm_leadToOpportunityConversion: {
    info: "Percent of leads that become opportunities (deals) over the selected period.",
    help: "Higher is better. Rule of thumb: <5% low, 5-15% typical, >15% strong for many B2B funnels (varies widely). Falling conversion often indicates lead quality or slow follow-up.",
    accountingNote: false,
  },
  crm_newOpportunities: {
    info: "Number of new deals created in the selected period.",
    help: "Higher is generally better. If opportunities fall, revenue may soften in future periods unless win rate or deal size increases.",
    accountingNote: false,
  },
  crm_openPipeline: {
    info: "Sum of deal amounts for open (not closed) deals in the selected pipeline.",
    help: "Higher can be better, but only if deals progress. Very high pipeline without movement can indicate weak qualification or stalled deals.",
    accountingNote: false,
  },
  crm_weightedPipeline: {
    info: "Open pipeline adjusted by stage probability (each deal amount multiplied by its stage probability).",
    help: "More realistic than raw pipeline. Use it for forecasting and capacity planning. If weighted pipeline falls, expect slower growth unless lead flow and conversion improve.",
    accountingNote: false,
  },
  crm_pipelineCoverage: {
    info: "How much pipeline you have relative to your near-term revenue target (pipeline or weighted pipeline divided by target).",
    help: "Rule of thumb: many teams aim for 2-4x coverage for the next 60-90 days. Low coverage signals growth risk; very high coverage can indicate weak qualification.",
    accountingNote: false,
  },
  crm_winRate: {
    info: "Closed-won deals divided by all closed deals (won + lost) in the selected period.",
    help: "Rule of thumb: 20-30% common; 30-50% strong; <15% often indicates qualification or pricing issues. Track by channel and deal type if possible.",
    accountingNote: false,
  },
  crm_averageDealSize: {
    info: "Average value of closed-won deals (or all deals, depending on the view).",
    help: "Bigger deals can accelerate growth but may lengthen sales cycles. Ensure deal amounts are consistently populated; compare to your baseline.",
    accountingNote: false,
  },
  crm_salesCycleLength: {
    info: "Typical number of days from deal creation to closed-won (median is used to reduce outliers).",
    help: "Shorter is usually better. Rule of thumb: 15-60 days is common for SMB services; 60-120 days for larger or complex deals. A lengthening cycle suggests decision friction or unclear scope.",
    accountingNote: false,
  },
  crm_closedWonAmount: {
    info: "Total value of deals won in the selected period.",
    help: "Higher is better. If bookings rise but revenue does not, review onboarding speed, billing timing, and delivery capacity.",
    accountingNote: false,
  },
  crm_closedWonCount: {
    info: "Number of deals won in the selected period.",
    help: "Higher is generally better. If count rises but amount does not, you may be winning smaller deals; check profitability and delivery load.",
    accountingNote: false,
  },
  crm_forecastNextPeriod: {
    info: "Expected near-term bookings based on weighted pipeline and expected close dates (directional estimate).",
    help: "Directional, not guaranteed. If forecast is below target, increase demand generation or improve conversion by tightening qualification and follow-up.",
    accountingNote: false,
  },
  crm_pipelineVelocity: {
    info: "A measure of how fast revenue is moving through the pipeline (commonly: opportunities x win rate x average deal size divided by sales cycle length).",
    help: "Higher is better. If velocity drops, diagnose which component is falling: fewer opportunities, lower win rate, smaller deals, or longer cycle.",
    accountingNote: false,
  },

  /* Section 6. HR */
  hr_headcount: {
    info: "Number of active employees as of the selected date (or end of period).",
    help: "Context metric. Headcount should align with revenue and delivery capacity. Rapid headcount growth without revenue growth can pressure margins.",
    accountingNote: false,
  },
  hr_headcountChangeMoM: {
    info: "Net change in headcount versus the prior month (new hires minus separations).",
    help: "Use to spot staffing acceleration or contraction. Large swings can create onboarding load or knowledge gaps; watch turnover drivers.",
    accountingNote: false,
  },
  hr_newHires: {
    info: "Count of employees with a start date in the selected period.",
    help: "Not inherently good or bad. Compare to growth plans and turnover. If hires are rising while turnover is also rising, investigate fit, pay, and management load.",
    accountingNote: false,
  },
  hr_separations: {
    info: "Count of employees who left in the selected period (voluntary plus involuntary).",
    help: "Lower is usually better. Spikes can indicate engagement issues, pay pressure, or seasonal staffing patterns.",
    accountingNote: false,
  },
  hr_turnoverRate: {
    info: "Separations divided by average headcount for the period (often annualized).",
    help: "Rule of thumb (annual): <10% strong, 10-20% watch, >20% high for many roles (varies by industry). Segment by department or role for clearer action.",
    accountingNote: false,
  },
  hr_laborCost: {
    info: "Total labor cost for the period, including gross wages, employer taxes, and benefits (where available).",
    help: "Use for cost control and planning. A rising labor cost with flat revenue often compresses margins unless productivity improves.",
    accountingNote: true,
  },
  hr_laborPercentOfRevenue: {
    info: "Total labor cost divided by revenue for the same period.",
    help: "Good range depends on business model. Rule of thumb: service firms often run 30-70%; product firms often lower. Watch trend and compare to peer benchmarks when available.",
    accountingNote: true,
  },
  hr_overtimePercent: {
    info: "Overtime hours (or overtime pay) divided by total hours (or pay) for the selected period.",
    help: "Lower is generally better. Rule of thumb: keep steady-state overtime under 5-10%. Sustained higher overtime suggests understaffing, scheduling issues, or demand spikes.",
    accountingNote: false,
  },
  hr_averageCostPerEmployee: {
    info: "Total labor cost divided by average headcount for the period.",
    help: "Use to track wage inflation and benefits changes. Rising cost per employee is normal in tight labor markets; compare to revenue per employee to ensure productivity keeps pace.",
    accountingNote: true,
  },
  hr_revenuePerEmployee: {
    info: "Revenue for the period divided by average headcount.",
    help: "Higher is generally better and indicates productivity. Compare to prior periods and peers. If revenue per employee falls, review pricing, utilization, and staffing mix.",
    accountingNote: true,
  },
  hr_timeToHire: {
    info: "Typical number of days from job open date to accepted offer or hire date (median).",
    help: "Lower is generally better. Rule of thumb: 30-60 days is common; >90 days suggests pipeline or process issues. Segment by role type.",
    accountingNote: false,
  },
  hr_absencePtoRate: {
    info: "PTO or absence hours divided by total available work hours for the period (where available).",
    help: "Context metric. Extremely high rates can signal burnout or staffing gaps; extremely low can signal underuse of PTO and burnout risk.",
    accountingNote: false,
  },

  /* Section 7. Banking */
  bank_totalBalance: {
    info: "Sum of balances across connected bank accounts as of the selected date.",
    help: "Rule of thumb: aim to cover at least 1-3 months of operating expenses (including payroll); 3-6 months if revenue is volatile, seasonal, or you are investing heavily.",
    accountingNote: true,
  },
  bank_operatingCashBuffer: {
    info: "Estimated months the business can operate using current bank cash based on recent average monthly expenses (cash divided by average monthly spend).",
    help: "Rule of thumb: 6+ months comfortable, 3-6 months workable, 1-3 tight, <1 urgent. Use the trend: a steadily declining buffer is an early warning sign.",
    accountingNote: false,
  },
  bank_cashInflows30day: {
    info: "Total cash received into bank accounts over the last 30 days (or selected period).",
    help: "Higher is generally better, but confirm inflows are from core operations (customer payments) vs financing or one-time items. Compare to prior periods for momentum.",
    accountingNote: false,
  },
  bank_cashOutflows30day: {
    info: "Total cash paid out from bank accounts over the last 30 days (or selected period).",
    help: "Lower is generally better unless spending is intentional for growth. Large spikes should be reviewed for one-time payments, tax payments, or vendor timing.",
    accountingNote: false,
  },
  bank_netCashChange30day: {
    info: "Cash inflows minus cash outflows over the last 30 days (or selected period).",
    help: "Positive is generally better. Multiple negative periods plus declining cash buffer indicates tightening liquidity.",
    accountingNote: false,
  },
  bank_averageDailyBalance: {
    info: "Average of daily bank balances over the selected period.",
    help: "Context metric. Use to understand typical liquidity rather than a single-day snapshot. A declining average daily balance is a stronger signal than a one-day dip.",
    accountingNote: false,
  },
  bank_largestOutflowCategories: {
    info: "The top spending categories by cash outflow over the selected period.",
    help: "Use to identify what is driving cash burn (payroll, rent, vendors, debt). Large changes in top categories often explain margin and cash movements.",
    accountingNote: false,
  },
  bank_lineOfCreditAvailable: {
    info: "Unused amount available on a revolving credit line (credit limit minus outstanding balance).",
    help: "More available credit increases flexibility. Rule of thumb: keep a meaningful cushion; if availability is near zero, options are limited during shocks.",
    accountingNote: false,
  },
  bank_lineOfCreditUtilization: {
    info: "Outstanding balance divided by total credit limit on the line of credit.",
    help: "Lower is generally safer. Rule of thumb: <50% leaves flexibility; 50-80% watch; >80% can signal reliance on credit and reduced capacity.",
    accountingNote: false,
  },
  bank_debtBalance: {
    info: "Total outstanding debt across loans and credit facilities (principal balance).",
    help: "Context metric. Rising debt can fund growth, but increases fixed obligations. Compare debt trend to revenue, profit, and cash buffer.",
    accountingNote: true,
  },
  bank_interestExpenseMonthly: {
    info: "Total interest cost paid or accrued during the selected period.",
    help: "Lower is better. Rising interest expense can signal higher rates or higher debt. Watch interest coverage and cash buffer when rates rise.",
    accountingNote: true,
  },
  bank_dscr: {
    info: "Operating cash flow (or EBITDA) divided by total debt payments (principal plus interest) for the period.",
    help: "Higher is better. Rule of thumb: 1.25x+ is a common lender comfort threshold; 1.0-1.25x watch; <1.0x indicates the business is not generating enough to cover debt service.",
    accountingNote: false,
  },

  /* Section 8. Data Analytics (Data Health and Insights) */
  data_sourcesConnected: {
    info: "Count of connected systems (for example, accounting, CRM, payroll or HR, banking).",
    help: "More sources can improve insight, but only if data is clean and refreshed. For MVP, prioritize reliable accounting and CRM connections first.",
    accountingNote: false,
  },
  data_connectionStatus: {
    info: "Whether each source system is currently authenticated and syncing (Connected, Needs re-auth, or Error).",
    help: "All core sources should show Connected. Any system in Needs re-auth or Error will cause missing or stale metrics.",
    accountingNote: false,
  },
  data_lastSyncAccounting: {
    info: "Timestamp of the most recent successful data pull from the accounting system.",
    help: "Rule of thumb: within 24 hours is healthy for daily dashboards; 24-72 hours watch; >72 hours likely means credentials or API issues.",
    accountingNote: false,
  },
  data_lastSyncCrm: {
    info: "Timestamp of the most recent successful data pull from the CRM.",
    help: "Rule of thumb: within 24 hours healthy; >72 hours can materially skew pipeline and forecast views.",
    accountingNote: false,
  },
  data_freshness: {
    info: "Hours since the last successful sync for each connected system.",
    help: "Lower is better. If freshness is high, treat metrics as stale until the next successful sync.",
    accountingNote: false,
  },
  data_unmappedAccounts: {
    info: "Number of accounting accounts or report lines that are not mapped into standard categories (Revenue, COGS, OpEx, etc.).",
    help: "Lower is better. Rule of thumb: 0 is ideal; <5 is manageable; >10 can skew margins and benchmarks. Prioritize mapping for top-dollar accounts first.",
    accountingNote: false,
  },
  data_mappingCoverage: {
    info: "Share of total dollars (or accounts) successfully mapped into standard categories.",
    help: "Higher is better. Rule of thumb: 90%+ is strong for reliable reporting; below that, review unmapped items.",
    accountingNote: false,
  },
  data_missingDealAmounts: {
    info: "Percent of open deals missing an amount value (or missing expected monthly value) in the CRM.",
    help: "Lower is better. Rule of thumb: keep under 5-10%. Higher missing rates reduce forecast quality and pipeline accuracy.",
    accountingNote: false,
  },
  data_benchmarkCoverage: {
    info: "Whether peer benchmarks are available for the selected industry and size band (and which KPIs have benchmarks).",
    help: "More coverage is better. If benchmarks are missing, use historical baseline comparisons (trailing 6-12 months) as the default reference.",
    accountingNote: false,
  },
  data_alertsTriggered: {
    info: "Count of automated alerts triggered by rules (for example, cash declining plus overdue AR rising).",
    help: "Not inherently good or bad. A rising alert count signals volatility or data issues. Review alerts for root cause and confirm they are not driven by stale data.",
    accountingNote: false,
  },
  data_anomaliesDetected: {
    info: "Count of unusual changes detected in KPIs or transactions (for example, expense spike vs baseline).",
    help: "Use as a prompt to investigate, not as a final conclusion. Confirm anomalies against accounting period timing and one-time events.",
    accountingNote: false,
  },
  data_forecastAccuracy: {
    info: "Average percent error between forecasted and actual values over recent periods (mean absolute percentage error).",
    help: "Lower is better. Rule of thumb: <10-15% is strong; 15-30% workable; >30% indicates the forecast inputs or process need improvement. Accuracy improves as more history accumulates.",
    accountingNote: false,
  },
  data_peerMedian: {
    info: "The 50th percentile (middle) value for comparable companies in the selected industry and size band.",
    help: "Use as the reference point. Above median is better for higher-is-better metrics; below median is better for lower-is-better metrics.",
    accountingNote: false,
  },
  data_peerRange: {
    info: "Typical peer range shown as the 25th-75th percentile band.",
    help: "Values inside the range are common for peers. Outside the range may indicate an outlier or differences in accounting classification.",
    accountingNote: false,
  },
  data_percentileRank: {
    info: "Where your metric sits compared to peers (for example, 70th percentile means better than 70% of peers for higher-is-better metrics).",
    help: "Higher percentile is better for higher-is-better metrics; lower percentile is better for lower-is-better metrics. Use to prioritize improvement areas.",
    accountingNote: false,
  },
  data_industryNewsVolume: {
    info: "Count of recent news articles that match your industry keywords over the selected period.",
    help: "More news is not always good or bad. Spikes often indicate unusual activity or change; use themes and major events to understand what is driving coverage.",
    accountingNote: false,
  },
  data_topNewsThemes: {
    info: "Most common topics appearing in recent industry coverage.",
    help: "Use themes to quickly understand what is happening in the market (regulation, supply chain, pricing, labor, etc.).",
    accountingNote: false,
  },
  data_majorEventsAlerts: {
    info: "Highlighted items from industry coverage that meet an alert threshold (regulatory changes or major disruptions).",
    help: "Use as a prompt for management attention. Alerts are directional; review the source summary for details.",
    accountingNote: false,
  },

  /* Balance Sheet subsection (Liabilities and Equities page) */
  bs_currentAssets: {
    info: "Assets expected to convert to cash within 12 months (for example, cash, AR, inventory).",
    help: "Higher current assets support short-term operations. Watch composition: heavy inventory or slow AR is less useful than cash.",
    accountingNote: true,
  },
  bs_fixedAssets: {
    info: "Long-term assets used to run the business (for example, equipment, vehicles, buildings).",
    help: "Fixed assets support capacity but tie up cash. Rising fixed assets should align with growth or replacement plans.",
    accountingNote: true,
  },
  bs_totalAssets: {
    info: "Sum of current assets plus fixed assets plus other assets.",
    help: "Context metric. Compare growth in total assets to growth in revenue and profit; asset-heavy growth without matching returns can erode returns on capital.",
    accountingNote: true,
  },
  bs_currentLiabilities: {
    info: "Liabilities due within 12 months (for example, accounts payable, credit cards, payroll taxes).",
    help: "Large current liabilities require near-term cash. If current liabilities rise while cash falls, prioritize collections and review payment schedules.",
    accountingNote: true,
  },
  bs_longTermLiabilities: {
    info: "Liabilities due beyond 12 months (for example, term loans).",
    help: "Long-term debt can support growth if cash flow covers payments. Compare to EBITDA and interest coverage ratios.",
    accountingNote: true,
  },
  bs_accountsPayable: {
    info: "Total unpaid vendor bills recorded as accounts payable on the Balance Sheet.",
    help: "Moderate AP is normal. High overdue AP or rapidly rising AP can indicate cash stress or vendor payment delays.",
    accountingNote: true,
  },
  bs_creditCardBalances: {
    info: "Outstanding balances on business credit cards as of the selected date.",
    help: "Credit cards can bridge short-term needs, but high balances can create interest drag. Watch utilization and ensure a payoff plan.",
    accountingNote: true,
  },
  bs_loansPayable: {
    info: "Outstanding principal balance of loans and notes payable.",
    help: "Debt should be sized to cash generation. Rule of thumb: if debt service is crowding out operating cash needs, refinancing or cost actions may be required.",
    accountingNote: true,
  },
  bs_payrollTaxLiabilities: {
    info: "Amounts owed for payroll taxes, benefits, and related liabilities as of the selected date.",
    help: "Should generally be paid on schedule. Chronic buildup can create compliance risk and penalties.",
    accountingNote: true,
  },
  bs_ownersEquity: {
    info: "Owners' residual interest in the business: assets minus liabilities.",
    help: "Higher equity provides stability and borrowing capacity. Falling equity can indicate sustained losses or large distributions.",
    accountingNote: true,
  },
  bs_retainedEarnings: {
    info: "Cumulative profit kept in the business over time (net income minus distributions) shown on the Balance Sheet.",
    help: "Growing retained earnings generally indicates the business is building value. Declines often reflect losses or aggressive owner draws.",
    accountingNote: true,
  },
  bs_liabilitiesPlusEquity: {
    info: "Total liabilities plus total equity (should equal total assets on the Balance Sheet).",
    help: "If this does not equal total assets, it can indicate data mapping issues or an incomplete balance sheet sync.",
    accountingNote: true,
  },

  /* Sales page metrics */
  sales_totalRevenue: {
    info: "Total revenue recognized in the selected period (from the Profit and Loss).",
    help: "Higher is better. Use trend and compare to peer benchmarks when available.",
    accountingNote: true,
  },
  sales_growthMoM: {
    info: "Percent change in revenue versus the prior period.",
    help: "Positive is generally healthy. Multiple negative periods in a row merits review of pipeline and retention.",
    accountingNote: true,
  },
  sales_transactionCount: {
    info: "Count of sales transactions in the period (for example, invoices, sales receipts, or other sales documents, depending on system setup).",
    help: "More transactions can indicate volume growth; fewer transactions with flat revenue can indicate larger deal sizes. Interpret alongside average sale value.",
    accountingNote: false,
  },
  sales_averageSaleValue: {
    info: "Total sales divided by number of sales transactions in the period.",
    help: "Higher can be good if margins hold. Sudden drops may indicate discounting or lower-value work; sudden increases may indicate concentration risk or lumpy enterprise deals.",
    accountingNote: false,
  },
  sales_topCustomerShare: {
    info: "Percent of total sales coming from the top customer in the period (requires customer-level sales data).",
    help: "Lower concentration is typically safer. Rule of thumb: >20-30% from one customer is a material concentration risk for many SMBs.",
    accountingNote: false,
  },
  sales_top5CustomersShare: {
    info: "Percent of total sales coming from the top 5 customers (requires customer-level sales data).",
    help: "Lower concentration is typically safer. Use this to gauge customer diversification and renewal risk.",
    accountingNote: false,
  },
  sales_byProductService: {
    info: "Sales grouped by item, product line, or service category (when tracked in the accounting system).",
    help: "Use to identify what drives growth and margin. Best when items or classes are consistently used.",
    accountingNote: true,
  },
  sales_byLocationClassChannel: {
    info: "Sales segmented by class, location, or channel fields (when configured).",
    help: "Use to understand which segments are growing or declining. If segments are missing or inconsistent, treat as directional.",
    accountingNote: true,
  },
  sales_bookings: {
    info: "Total value of deals marked closed-won in the selected period (from the CRM).",
    help: "Healthy businesses maintain a steady flow of bookings. Compare bookings trend to revenue trend to spot delivery lag or invoicing delays.",
    accountingNote: false,
  },

  /* Expenses by Category page */
  exp_totalExpenses: {
    info: "Sum of expenses for the selected period (from the Profit and Loss).",
    help: "Lower is better relative to revenue. If expenses grow faster than revenue, margins compress and cash pressure rises.",
    accountingNote: true,
  },
  exp_categorySpend: {
    info: "Total dollars spent in a specific expense category (account or grouped accounts) during the period.",
    help: "Use to identify the largest cost drivers. Focus on categories that are large and rising quickly.",
    accountingNote: true,
  },
  exp_categoryShare: {
    info: "Category spend divided by total expenses.",
    help: "Higher share means that category is a bigger driver of profitability. Changes in share can reveal cost creep or reclassification.",
    accountingNote: true,
  },
  exp_categoryChangeDollars: {
    info: "Difference in category spend versus the prior period (category spend this period minus prior period).",
    help: "Large increases should be explained: new hires, one-time purchases, timing, or misclassification.",
    accountingNote: true,
  },
  exp_categoryChangePercent: {
    info: "Percent change in category spend versus the prior period.",
    help: "Use to spot fast-growing categories. A sustained increase often signals a structural cost change that should be budgeted.",
    accountingNote: true,
  },
  exp_topCategories: {
    info: "The highest-spend categories during the selected period.",
    help: "Review these regularly. Small improvements in top categories usually have the biggest impact on margin and cash.",
    accountingNote: true,
  },
  exp_oneTimeVsRecurring: {
    info: "Optional classification separating recurring costs (subscriptions, rent) from one-time items (equipment, one-off projects).",
    help: "Recurring cost growth is more concerning because it commits future cash. One-time costs should be planned and compared to runway.",
    accountingNote: false,
  },

  /* Conversion Cycles page */
  cc_dso: {
    info: "Estimated average days to collect accounts receivable. Typically computed as (average AR / revenue) * days in period.",
    help: "Lower is better. Rule of thumb: <30 days strong; 30-60 typical; >60 indicates collection friction and higher cash risk (industry varies).",
    accountingNote: false,
  },
  cc_dpo: {
    info: "Estimated average days to pay accounts payable. Typically computed as (average AP / spend) * days in period.",
    help: "Neither too high nor too low is best. Very high DPO can strain vendors and signal cash stress; very low DPO can drain cash. Compare to your payment terms.",
    accountingNote: false,
  },
  cc_dio: {
    info: "Estimated average days inventory is held before being sold. Typically (average inventory / COGS) * days in period (inventory businesses only).",
    help: "Lower is generally better (faster turnover), but targets vary widely by industry. If DIO rises, cash is tied up longer in inventory.",
    accountingNote: false,
  },
  cc_operatingCycle: {
    info: "Time to convert inventory and receivables into cash: DIO + DSO (inventory businesses). If no inventory, operating cycle is approximately DSO.",
    help: "Shorter is generally better. Longer cycles require more working capital and cash buffer.",
    accountingNote: false,
  },
  cc_cashConversionCycle: {
    info: "Net time between paying suppliers and collecting from customers: DSO + DIO - DPO.",
    help: "Lower is better. A negative CCC can be excellent (you get paid before paying vendors). Rising CCC often signals worsening working capital or slower collections.",
    accountingNote: false,
  },
  cc_arTurnover: {
    info: "How many times per year receivables are collected: 365 / DSO (approximate).",
    help: "Higher turnover is better (faster collection). Use trends over time and compare to peers when available.",
    accountingNote: false,
  },
  cc_apTurnover: {
    info: "How many times per year payables are paid: 365 / DPO (approximate).",
    help: "Lower turnover means paying slower; higher turnover means paying faster. Align with your cash strategy and vendor terms.",
    accountingNote: false,
  },
  cc_inventoryTurnover: {
    info: "How many times inventory is sold per year: 365 / DIO (approximate).",
    help: "Higher turnover is usually better (less cash tied up), but targets vary by industry. Sudden drops can signal overstocking or demand slowdown.",
    accountingNote: false,
  },
  cc_overdueAR: {
    info: "Share of accounts receivable that is past due (non-current aging buckets) divided by total AR.",
    help: "Lower is better. >25% often indicates meaningful collections friction; combine with DSO and cash trend for risk assessment.",
    accountingNote: false,
  },
  cc_overdueAP: {
    info: "Share of accounts payable that is past due divided by total AP.",
    help: "Lower is better. Rising overdue AP percent can indicate cash stress and vendor relationship risk.",
    accountingNote: false,
  },
};
